import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _extends from "@babel/runtime/helpers/extends";
var _excluded = ["children", "theme"],
    _excluded2 = ["ssr", "defaultWidth"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import styled, { withTheme } from 'styled-components';
import React from 'react';
import PropTypes from 'prop-types';
import Box from '../Box';
import { H1 } from '../Heading';
import { useThemeDefaults } from '../utils';
import Text, { BaseText } from '../Text/Text';
import Sheet from '../Sheet';
import Link from '../Link';
import Breakpoint from '../Breakpoint';
import DownIcon from '../Icons/DownIcon';
import RightIcon from '../Icons/RightIcon';
import MenuList, { HR, Item as MenuItem } from '../MenuList';
import NavMenu from '../NavMenu';
import { styleFromVariant } from '../theme';
import { mapScaledPropValue } from '../scales';
var VARIANT_PRIMARY = 'primary';
var VARIANT_SECONDARY = 'secondary';
var VariantContext = /*#__PURE__*/React.createContext(VARIANT_PRIMARY);

var isSecondaryVariant = function isSecondaryVariant(variant) {
  return variant === VARIANT_SECONDARY;
};

var withVariant = function withVariant(Component) {
  return function (props) {
    return /*#__PURE__*/React.createElement(VariantContext.Consumer, null, function (variant) {
      return /*#__PURE__*/React.createElement(Component, _extends({
        variant: variant
      }, props));
    });
  };
};

var TabOuter = useThemeDefaults(function (_ref) {
  var theme = _ref.theme;
  return {
    as: 'li',
    cursor: 'pointer',
    mr: theme.tabs.tabMarginRight
  };
}, Box);
export var TabInner = withVariant(useThemeDefaults(function (_ref2) {
  var active = _ref2.active,
      variant = _ref2.variant,
      theme = _ref2.theme;
  return {
    borderBottom: theme.tabs.activeUnderline,
    borderColor: active ? 'currentColor' : 'transparent',
    hoverTextDecoration: 'none',
    as: 'a',
    display: 'block',
    pb: mapScaledPropValue(function (textStyle) {
      return "".concat(theme.space[isSecondaryVariant(variant) ? theme.tabs.secondary.spaceBelow : theme.tabs.spaceBelow] - theme.lineSpacings[styleFromVariant(textStyle)].bottom - 1, "px");
    }, theme.tabs.textStyle)
  };
}, styled(Link).withConfig({
  displayName: "TabbedHeader__TabInner",
  componentId: "sc-yge4mx-0"
})(function (_ref3) {
  var theme = _ref3.theme;
  return {
    ':hover': {
      borderBottom: theme.tabs.activeUnderline
    }
  };
})));
export var Tab = function Tab(props) {
  return /*#__PURE__*/React.createElement(TabOuter, _extends({
    variant: props.variant
  }, props.liProps), /*#__PURE__*/React.createElement(TabInner, props));
};
process.env.NODE_ENV !== "production" ? Tab.propTypes = _objectSpread({
  liProps: PropTypes.object
}, Link.propTypes) : void 0;
var TabsBase = /*#__PURE__*/React.forwardRef(function (props, ref) {
  return /*#__PURE__*/React.createElement(VariantContext.Provider, {
    value: props.variant
  }, /*#__PURE__*/React.createElement(BaseText, _extends({
    ref: ref
  }, props)));
});
export var Tabs = useThemeDefaults(function (_ref4) {
  var variant = _ref4.variant,
      theme = _ref4.theme;
  return {
    as: 'ul',
    role: 'navigation',
    display: 'flex',
    color: theme.tabs.color,
    textStyle: isSecondaryVariant(variant) ? theme.tabs.secondary.textStyle : theme.tabs.textStyle,
    flex: '1 1 auto',
    whiteSpace: 'nowrap',
    textAlign: ['right', 'left']
  };
}, TabsBase);
Tabs.propTypes = _objectSpread(_objectSpread({}, BaseText.propTypes), {}, {
  variant: PropTypes.oneOf([VARIANT_PRIMARY, VARIANT_SECONDARY])
});
/**
 * Given an array of jsx children, pick one as the "active" child, and
 * put the rest into an array. The "active" child will be the first child
 * with the `active` prop set to `true` or the first child, if none have
 * the `active` prop set to `true`.
 *
 * If the input is empty, the value of `active` is set to `null`, and
 * `links` to `[]`.
 * @param {JSXElement[]} childrenArr
 * @returns {object} { active: JSXElement, links: JSXElement[] }
 */

export var groupLinks = function groupLinks(childrenArr) {
  var _childrenArr$reduce = childrenArr.reduce(function (_ref5, child) {
    var active = _ref5.active,
        links = _ref5.links;
    return child.props.active && !active ? {
      active: child,
      links: links
    } : {
      active: active,
      links: [].concat(_toConsumableArray(links), [child])
    };
  }, {
    active: null,
    links: []
  }),
      active = _childrenArr$reduce.active,
      links = _childrenArr$reduce.links;

  return {
    active: active ? active : links.shift(),
    links: links
  };
};

var HeaderLinksSheetContent = function HeaderLinksSheetContent(_ref6) {
  var hide = _ref6.hide,
      links = _ref6.links;
  return links.length ? /*#__PURE__*/React.createElement(MenuList, null, links.map(function (link, i) {
    return /*#__PURE__*/React.createElement(MenuItem, _extends({
      as: "a",
      key: i
    }, link.props, {
      liProps: {
        onClick: hide
      }
    }));
  })) : null;
};

var _ref8 = /*#__PURE__*/React.createElement(HR, {
  mt: 0
});

var HeaderLinksSheetSmContent = function HeaderLinksSheetSmContent(_ref7) {
  var hide = _ref7.hide,
      links = _ref7.links;
  return links.length ? /*#__PURE__*/React.createElement(NavMenu, {
    variant: "mobile",
    width: "100%"
  }, _ref8, /*#__PURE__*/React.createElement(NavMenu.Links, null, links.map(function (link, i) {
    return /*#__PURE__*/React.createElement(NavMenu.Link, _extends({
      key: i
    }, link.props, {
      liProps: {
        onClick: hide
      }
    }));
  }))) : null;
};

export var HeaderLinksMenu = withTheme(function (_ref9) {
  var children = _ref9.children,
      theme = _ref9.theme,
      props = _objectWithoutProperties(_ref9, _excluded);

  var _groupLinks = groupLinks(React.Children.toArray(children)),
      active = _groupLinks.active,
      links = _groupLinks.links;

  var activeChildren = active ? active.props.children : null;
  return activeChildren ? /*#__PURE__*/React.createElement(Sheet, {
    content: function content(_ref10) {
      var hide = _ref10.hide;
      return /*#__PURE__*/React.createElement(HeaderLinksSheetContent, {
        hide: hide,
        links: links
      });
    },
    target: function target(_ref11) {
      var ref = _ref11.ref,
          setHidden = _ref11.setHidden,
          isHidden = _ref11.isHidden;
      return /*#__PURE__*/React.createElement(Tabs, _extends({
        as: "button",
        cursor: "pointer",
        sb: theme.tabbedHeader.spaceBelow,
        ref: ref,
        flex: "none",
        alignItems: "baseline",
        onClick: function onClick() {
          return setHidden(!isHidden);
        }
      }, props), activeChildren, ' ', links.length > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(DownIcon, {
        display: ['none', 'inline'],
        ml: "2px",
        width: theme.tabs.downIconSize,
        height: theme.tabs.downIconSize
      }), /*#__PURE__*/React.createElement(RightIcon, {
        display: ['inline', 'none'],
        position: "relative",
        top: "2px",
        ml: "2px",
        width: theme.tabs.rightIconSize,
        height: theme.tabs.rightIconSize
      })));
    },
    smContent: function smContent(_ref12) {
      var hide = _ref12.hide;
      return /*#__PURE__*/React.createElement(HeaderLinksSheetSmContent, {
        hide: hide,
        links: links
      });
    }
  }) : null;
});
export var ResponsiveTabs = function ResponsiveTabs(_ref13) {
  var ssr = _ref13.ssr,
      defaultWidth = _ref13.defaultWidth,
      props = _objectWithoutProperties(_ref13, _excluded2);

  return /*#__PURE__*/React.createElement(Breakpoint, {
    ssr: ssr,
    defaultWidth: defaultWidth
  }, function (_ref14) {
    var bpName = _ref14.bpName;
    return ['sm', 'md'].includes(bpName) ? /*#__PURE__*/React.createElement(HeaderLinksMenu, props) : /*#__PURE__*/React.createElement(Tabs, props);
  });
};
process.env.NODE_ENV !== "production" ? ResponsiveTabs.propTypes = _objectSpread(_objectSpread({}, Breakpoint.propTypes), Tabs.propTypes) : void 0;
export var Title = useThemeDefaults(function (_ref15) {
  var theme = _ref15.theme;
  return {
    as: 'h1',
    textStyle: theme.tabbedHeader.titleTextStyle,
    mr: theme.tabbedHeader.titleMarginRight,
    sb: theme.tabbedHeader.spaceBelow,
    color: theme.colors.title,
    truncate: true
  };
}, Text);
Title.propTypes = _objectSpread({}, H1.propTypes);
var TabbedHeader = useThemeDefaults(function (_ref16) {
  var theme = _ref16.theme;
  return {
    display: 'flex',
    alignItems: 'baseline',
    borderBottom: '1px solid',
    borderColor: theme.hrColor,
    justifyContent: ['space-between', 'flex-start']
  };
}, Box);
TabbedHeader.propTypes = _objectSpread({}, Box.propTypes);
TabbedHeader.Tabs = Tabs;
TabbedHeader.Tab = Tab;
TabbedHeader.Title = Title;
TabbedHeader.ResponsiveTabs = ResponsiveTabs;
export default TabbedHeader;